import { mkdir, writeFile } from 'node:fs/promises';
import { join, relative } from 'node:path';

import { engineLog } from '@/core/logger-registry.js';
import { getPrimaryStack } from '@/core/stack-profile.js';
import type { ClassificationResult } from '@/core/types/classification.js';
import type { PentestReportIndex, PentestRuntimeStatus } from '@/core/types/pentest.js';
import { PATHS } from '@/core/constants/paths.js';
import { toPosixPath } from '@/core/path-utils.js';
import { StackIntrospector } from '@/introspection/stack-introspector.js';
import { VERSION } from '@/index.js';
import {
  buildDependencyFindings,
  buildModuleFindings,
  buildRuntimeFindings,
  buildSecretFindings,
  assignFindingIds,
} from '@/pentest/findings.js';
import { FileCheckMapper } from '@/pentest/file-check-mapper.js';
import { IncrementalScanner } from '@/pentest/incremental-scanner.js';
import { queryOsv } from '@/pentest/osv.js';
import { PentestProgressTracker, runArtifactsDir, runLogsDir } from '@/pentest/progress-tracker.js';
import { buildPentestMarkdown, remediationPriorities } from '@/pentest/report-builder.js';
import {
  discoverTargetUrl,
  hashRelevantInputs,
  inferSourceArtifacts,
  loadModuleDocs,
  loadTests,
  parseSuspiciousRoutes,
  readJsonIfExists,
  runProjectScript,
  skillPath,
  summarizeCommandAvailability,
  toLocalTimestamp,
  toReportId,
  toolReferencePath,
  writeJson,
} from '@/pentest/shared.js';

export interface PentestWorkflowOptions {
  projectRoot: string;
  classification: ClassificationResult;
  targetUrl?: string;
  focusModules?: string[];
  dbConnectionName?: string;
  incremental?: boolean;
  lastRunId?: string;
}

export interface PentestWorkflowResult {
  run_id: string;
  report_id: string;
  report_path: string;
  sidecar_path: string;
}

const PENTEST_STEPS = [
  { id: 'collect-context', title: 'Collect docs and test context' },
  { id: 'run-project-scripts', title: 'Run project-owned pentest scripts' },
  { id: 'live-validation', title: 'Run safe runtime validation' },
  { id: 'synthesize-findings', title: 'Normalize findings from collected evidence' },
  { id: 'write-report', title: 'Write canonical pentest report' },
] as const;

const PENTEST_SKILLS = {
  context: ['permission-boundary-review', 'business-logic-abuse-review'],
  dependency: ['dependency-advisory-triage'],
  runtime: ['runtime-surface-probing'],
  normalize: ['finding-normalizer'],
} as const;

export class PentestWorkflow {
  private readonly introspector = new StackIntrospector();
  private readonly tracker = new PentestProgressTracker();

  async run(options: PentestWorkflowOptions): Promise<PentestWorkflowResult> {
    const snapshot = await this.introspector.snapshot(options.projectRoot);
    const stack = getPrimaryStack({
      routing: { domain: 'coding' },
      stack_profile: snapshot.profile,
    });
    const targetUrl =
      options.targetUrl ??
      extractUrlFromText(options.classification.request_text) ??
      (await discoverTargetUrl(options.projectRoot, stack));
    const focusModules = options.focusModules ?? [];

    if (options.incremental && options.lastRunId) {
      const frameworks = [stack, ...snapshot.profile.traits];
      const mapper = new FileCheckMapper(frameworks);
      const scanner = new IncrementalScanner(mapper);
      const scanResult = await scanner.scan({
        projectRoot: options.projectRoot,
        lastRunId: options.lastRunId,
        frameworks,
      });

      if (scanResult.no_security_changes) {
        engineLog(
          'info',
          '[pentest] No security-relevant changes detected since last run. Skipping incremental pentest.',
        );
        return {
          run_id: options.lastRunId,
          report_id: '',
          report_path: '',
          sidecar_path: '',
        };
      }
    }

    const progress = await this.tracker.ensureRun({
      projectRoot: options.projectRoot,
      workflow: 'pentest',
      targetUrl,
      steps: [...PENTEST_STEPS],
    });
    progress.target_url = targetUrl ?? null;
    progress.incremental_type = options.incremental ? 'incremental' : 'full';
    await this.tracker.save(options.projectRoot, progress);

    const artifactsDir = runArtifactsDir(options.projectRoot, progress.run_id);
    const logsDir = runLogsDir(options.projectRoot, progress.run_id);
    await mkdir(artifactsDir, { recursive: true });
    await mkdir(logsDir, { recursive: true });

    const blockedChecks: string[] = [];

    const contextHash = await hashRelevantInputs(options.projectRoot, 'pentest', {
      focus_modules: focusModules,
    });
    if (!this.tracker.shouldSkipStep(progress, 'collect-context', contextHash)) {
      this.tracker.markStepRunning(progress, 'collect-context', contextHash);
      await this.tracker.save(options.projectRoot, progress);

      const docs = await loadModuleDocs(options.projectRoot, focusModules);
      const tests = await loadTests(options.projectRoot, focusModules);
      const docsPath = join(artifactsDir, 'docs-summary.json');
      const testsPath = join(artifactsDir, 'tests-summary.json');
      await writeJson(docsPath, docs);
      await writeJson(
        testsPath,
        tests.map((test) => ({
          path: test.path,
          summary: test.content.slice(0, 500),
        })),
      );

      this.tracker.markStepCompleted(
        progress,
        'collect-context',
        [
          toPosixPath(relative(options.projectRoot, docsPath)),
          toPosixPath(relative(options.projectRoot, testsPath)),
        ],
        [...PENTEST_SKILLS.context].map(skillPath),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    const scriptHash = await hashRelevantInputs(options.projectRoot, 'pentest', {
      target_url: targetUrl,
      db_connection_name: options.dbConnectionName,
    });
    if (!this.tracker.shouldSkipStep(progress, 'run-project-scripts', scriptHash)) {
      this.tracker.markStepRunning(progress, 'run-project-scripts', scriptHash);
      await this.tracker.save(options.projectRoot, progress);

      const scriptEnv = {
        PENTEST_RUN_ID: progress.run_id,
        PENTEST_ARTIFACT_DIR: artifactsDir,
        PENTEST_TARGET_URL: targetUrl ?? '',
        PENTEST_DB_CONNECTION_NAME: options.dbConnectionName ?? '',
      };

      const scriptResults = await Promise.all([
        runProjectScript(options.projectRoot, 'pentest-audit-dependencies.sh', scriptEnv, logsDir),
        runProjectScript(options.projectRoot, 'pentest-scan-secrets.sh', scriptEnv, logsDir),
        runProjectScript(options.projectRoot, 'pentest-enumerate-surface.sh', scriptEnv, logsDir),
        runProjectScript(options.projectRoot, 'pentest-db-readonly.sh', scriptEnv, logsDir),
      ]);

      blockedChecks.push(...summarizeCommandAvailability(scriptResults));

      this.tracker.markStepCompleted(
        progress,
        'run-project-scripts',
        inferSourceArtifacts(artifactsDir, scriptResults),
        [...PENTEST_SKILLS.dependency].map(skillPath),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    const runtimeHash = await hashRelevantInputs(options.projectRoot, 'pentest', {
      target_url: targetUrl,
      step: 'runtime',
    });
    if (!this.tracker.shouldSkipStep(progress, 'live-validation', runtimeHash)) {
      this.tracker.markStepRunning(progress, 'live-validation', runtimeHash);
      await this.tracker.save(options.projectRoot, progress);

      const runtimeArtifacts: string[] = [];
      if (targetUrl === null) {
        blockedChecks.push(
          'Runtime validation skipped because no local target URL could be discovered.',
        );
      } else {
        const result = await runProjectScript(
          options.projectRoot,
          'pentest-runtime-checks.sh',
          {
            PENTEST_RUN_ID: progress.run_id,
            PENTEST_ARTIFACT_DIR: artifactsDir,
            PENTEST_TARGET_URL: targetUrl,
          },
          logsDir,
        );
        runtimeArtifacts.push(...result.artifact_paths);
        if (result.status !== 'completed') {
          blockedChecks.push(result.summary);
        }
      }

      this.tracker.markStepCompleted(
        progress,
        'live-validation',
        runtimeArtifacts,
        [...PENTEST_SKILLS.runtime].map(skillPath),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    const synthesizeHash = await hashRelevantInputs(options.projectRoot, 'pentest', {
      target_url: targetUrl,
      focus_modules: focusModules,
      step: 'findings',
    });
    let report: PentestReportIndex | null = null;
    if (!this.tracker.shouldSkipStep(progress, 'synthesize-findings', synthesizeHash)) {
      this.tracker.markStepRunning(progress, 'synthesize-findings', synthesizeHash);
      await this.tracker.save(options.projectRoot, progress);

      report = await buildCurrentPentestReport({
        projectRoot: options.projectRoot,
        classification: options.classification,
        snapshot,
        targetUrl,
        focusModules,
        blockedChecks,
        artifactsDir,
        progressRunId: progress.run_id,
        reportTimestamp: new Date(progress.started_at),
      });

      const findingIndexPath = join(
        options.projectRoot,
        PATHS.PENTEST_RUNS_DIR,
        progress.run_id,
        'finding-index.json',
      );
      await writeJson(findingIndexPath, report.findings);

      this.tracker.markStepCompleted(
        progress,
        'synthesize-findings',
        [
          toPosixPath(relative(options.projectRoot, findingIndexPath)),
          ...report.raw_evidence_paths,
        ],
        [...PENTEST_SKILLS.normalize].map(skillPath),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    if (report === null) {
      const existingSidecar = progress.sidecar_path
        ? await readJsonIfExists<PentestReportIndex>(
            join(options.projectRoot, progress.sidecar_path),
          )
        : null;
      report = existingSidecar;
    }

    if (report === null) {
      throw new Error('Pentest report synthesis did not produce a report payload');
    }

    const writeHash = await hashRelevantInputs(options.projectRoot, 'pentest', {
      findings: report.findings.map((finding) => finding.id),
      step: 'write-report',
    });
    if (!this.tracker.shouldSkipStep(progress, 'write-report', writeHash)) {
      this.tracker.markStepRunning(progress, 'write-report', writeHash);
      await this.tracker.save(options.projectRoot, progress);

      await writeJson(
        join(options.projectRoot, PATHS.PENTEST_RUNS_DIR, progress.run_id, 'report-preview.json'),
        { report_id: report.report_id, findings: report.findings.length },
      );
      await writeJson(join(options.projectRoot, report.sidecar_path), report);
      await writeJson(
        join(options.projectRoot, PATHS.PENTEST_RUNS_DIR, progress.run_id, 'blocked-checks.json'),
        report.blocked_checks,
      );
      await mkdir(join(options.projectRoot, PATHS.PENTEST_DIR), { recursive: true });
      const markdown = buildPentestMarkdown(report);
      await writeJson(join(options.projectRoot, report.sidecar_path), report);
      await writeFile(join(options.projectRoot, report.report_path), markdown);

      this.tracker.markStepCompleted(
        progress,
        'write-report',
        [report.report_path, report.sidecar_path],
        [...PENTEST_SKILLS.normalize].map(skillPath),
      );
      this.tracker.markRunCompleted(
        progress,
        report.report_path,
        report.sidecar_path,
        report.findings.map((finding) => finding.id),
      );
      await this.tracker.save(options.projectRoot, progress);
    }

    return {
      run_id: progress.run_id,
      report_id: report.report_id,
      report_path: report.report_path,
      sidecar_path: report.sidecar_path,
    };
  }
}

export async function buildCurrentPentestReport(input: {
  projectRoot: string;
  classification: ClassificationResult;
  snapshot: Awaited<ReturnType<StackIntrospector['snapshot']>>;
  targetUrl: string | null;
  focusModules: string[];
  blockedChecks: string[];
  artifactsDir: string;
  progressRunId: string;
  reportTimestamp: Date;
}): Promise<PentestReportIndex> {
  const docs = await loadModuleDocs(input.projectRoot, input.focusModules);
  const tests = await loadTests(input.projectRoot, input.focusModules);
  const osvFindings = await queryOsv(input.snapshot.packages);
  const osvPath = join(input.artifactsDir, 'dependencies', 'osv-results.json');
  await writeJson(osvPath, osvFindings);

  const dependencyFindings = await buildDependencyFindings(
    input.snapshot,
    input.artifactsDir,
    osvFindings,
  );
  const moduleFindings = buildModuleFindings(docs, tests);
  const secretFindings = await buildSecretFindings(input.artifactsDir);

  const runtimePayload = await readJsonIfExists<{
    target_url?: string;
    reachable?: boolean;
    detail?: string;
    checks?: Array<{ path: string; status: number }>;
  }>(join(input.artifactsDir, 'runtime', 'runtime-checks.json'));
  const runtimeStatus: PentestRuntimeStatus = input.targetUrl
    ? runtimePayload?.reachable
      ? {
          target_url: input.targetUrl,
          status: 'reachable',
          detail: runtimePayload.detail ?? 'Runtime checks completed.',
        }
      : {
          target_url: input.targetUrl,
          status: 'unreachable',
          detail: runtimePayload?.detail ?? 'Runtime checks could not confirm reachability.',
        }
    : {
        target_url: null,
        status: 'not-configured',
        detail: 'No local target URL was configured or discovered.',
      };
  const runtimeFindings = buildRuntimeFindings(
    input.targetUrl,
    runtimeStatus,
    parseSuspiciousRoutes(runtimePayload?.checks ?? []),
  );

  const findings = assignFindingIds([
    ...dependencyFindings,
    ...moduleFindings,
    ...secretFindings,
    ...runtimeFindings,
  ]);
  const timestamp = toLocalTimestamp(input.reportTimestamp);
  const reportPath = toPosixPath(join(PATHS.PENTEST_DIR, `${timestamp}.md`));
  const sidecarPath = toPosixPath(join(PATHS.PENTEST_DIR, `${timestamp}.json`));
  const stack = getPrimaryStack({
    routing: { domain: 'coding' },
    stack_profile: input.snapshot.profile,
  });

  return {
    schema_version: '1',
    generated_by: 'paqad-ai',
    framework_version: VERSION,
    report_id: toReportId('PENTEST', input.reportTimestamp),
    workflow: 'pentest',
    generated_at: input.reportTimestamp.toISOString(),
    report_path: reportPath,
    sidecar_path: sidecarPath,
    source_report_path: null,
    source_report_id: null,
    stack: {
      primary: stack,
      traits: [...input.snapshot.profile.traits],
      toolchains: input.snapshot.toolchains.map(
        (toolchain) => `${toolchain.ecosystem}:${toolchain.package_manager}`,
      ),
    },
    runtime: runtimeStatus,
    coverage: {
      docs: docs.length > 0,
      tests: tests.length > 0,
      code: true,
      runtime: input.targetUrl !== null,
      database: false,
      blocked_checks: [...input.blockedChecks],
    },
    findings,
    blocked_checks: [...input.blockedChecks],
    sources_used: [
      `${PATHS.MODULES_DIR}/**`,
      'tests/**',
      ...input.snapshot.toolchains.map((toolchain) => toolchain.lockfile),
      ...[
        toolReferencePath(stack),
        ...Object.values(PENTEST_SKILLS).flat().map(skillPath),
        ...docs.flatMap((doc) => doc.paths),
      ],
    ],
    methodology: [
      'Used module docs, tests, code manifests, and lockfiles as the primary evidence layers.',
      'Ran project-owned scripts for dependency audit, secret scanning, surface enumeration, and runtime validation when available.',
      'Normalized advisories from native audits and OSV into stable findings.',
    ],
    next_remediation_priorities: remediationPriorities(findings),
    raw_evidence_paths: [
      toPosixPath(relative(input.projectRoot, osvPath)),
      ...[
        toPosixPath(join(PATHS.PENTEST_RUNS_DIR, input.progressRunId, 'finding-index.json')),
        toPosixPath(
          join(PATHS.PENTEST_RUNS_DIR, input.progressRunId, 'artifacts', 'docs-summary.json'),
        ),
        toPosixPath(
          join(PATHS.PENTEST_RUNS_DIR, input.progressRunId, 'artifacts', 'tests-summary.json'),
        ),
      ],
    ],
  };
}

function extractUrlFromText(requestText: string): string | null {
  const match = requestText.match(/https?:\/\/[^\s)]+/i);
  return match?.[0] ?? null;
}
